import React, { useEffect, useState } from 'react';
import { Loader2, Link2, Check, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

// Link / unlink cohorts for a programme straight from its Learning Health card.
export default function ProgramCohortEditor({ open, onOpenChange, program, organizationId, isDark, onSaved }) {
  const [cohorts, setCohorts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !program) return;
    setSelected(new Set(program.linked_cohort_ids || []));
    setLoading(true);
    const q = organizationId && organizationId !== 'all' ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
    Promise.all([
      api.get(`/segments/cohorts${q}`).then((r) => (r.data?.cohorts || []).map((c) => ({ id: c.id, name: c.name }))).catch(() => { toast.error('Could not load cohorts'); return []; }),
      api.get(`/segments/programs/${program.cohort_id}/cohort-suggestions`).then((r) => r.data?.suggestions || []).catch(() => []),
    ])
      .then(([cs, sg]) => { setCohorts(cs); setSuggestions(sg); })
      .finally(() => setLoading(false));
  }, [open, program, organizationId]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const unlinkedSuggestions = suggestions.filter((s) => !selected.has(s.cohort_id));
  const suggestionById = Object.fromEntries(suggestions.map((s) => [s.cohort_id, s]));
  const [rosterCounts, setRosterCounts] = useState({});
  const [rosterBusy, setRosterBusy] = useState(null);

  // Roster import: CSV with header row containing email and/or learner_id (+ optional name).
  const importRoster = async (cohortId, file) => {
    setRosterBusy(cohortId);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
      const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ''));
      const idx = (k) => header.indexOf(k);
      const [ie, il, iname] = [idx('email'), idx('learner_id'), idx('name')];
      if (ie === -1 && il === -1) throw new Error('CSV needs an "email" or "learner_id" column');
      const rows = lines.slice(1).map((l) => l.split(',').map((v) => v.trim().replace(/^"|"$/g, ''))).map((v) => ({ email: ie >= 0 ? v[ie] : '', learner_id: il >= 0 ? v[il] : '', name: iname >= 0 ? v[iname] : '' }));
      const res = await api.post(`/segments/cohorts/${cohortId}/roster`, { rows });
      setRosterCounts((m) => ({ ...m, [cohortId]: res.data.count }));
      toast.success(`Roster: ${res.data.added} added, ${res.data.updated} updated${res.data.skipped ? `, ${res.data.skipped} skipped` : ''} · ${res.data.count} learners`);
      api.get(`/segments/programs/${program.cohort_id}/cohort-suggestions`).then((r) => setSuggestions(r.data?.suggestions || [])).catch(() => {});
    } catch (err) { toast.error(`Roster import failed: ${err.response?.data?.detail || err.message}`); }
    finally { setRosterBusy(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/segments/programs/${program.cohort_id}`, { cohort_ids: Array.from(selected) });
      toast.success(`${program.cohort_name}: ${selected.size} cohort${selected.size === 1 ? '' : 's'} linked`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(`Save failed: ${err.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };

  if (!program) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-md', isDark && 'bg-[#0d1526] border-[#22304d] text-slate-100')} data-testid="program-cohort-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Link2 className="h-4 w-4" /> Cohorts in {program.cohort_name}</DialogTitle>
          <DialogDescription className="text-xs">Tick the cohorts this programme serves. Its LEI is computed from the linked cohorts' measures (unless programme-level measures exist).</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
          {unlinkedSuggestions.length > 0 && (
            <div className={cn('rounded-xl border p-3', isDark ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-teal-200 bg-teal-50/60')} data-testid="program-cohort-suggestions">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className={cn('text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1', isDark ? 'text-cyan-200' : 'text-teal-800')}><Sparkles className="h-3.5 w-3.5" /> Suggested cohorts</p>
                <button type="button" onClick={() => setSelected((s) => new Set([...s, ...unlinkedSuggestions.map((x) => x.cohort_id)]))} className={cn('text-[11px] font-semibold hover:underline', isDark ? 'text-cyan-200' : 'text-teal-700')} data-testid="program-cohort-suggest-all">Link all</button>
              </div>
              <ul className="space-y-1">
                {unlinkedSuggestions.slice(0, 4).map((s) => (
                  <li key={s.cohort_id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className={cn('text-xs font-semibold truncate', isDark ? 'text-slate-100' : 'text-slate-800')}>{s.name}</p>
                      <p className={cn('text-[10px] truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>{s.reasons.join(' · ')}</p>
                    </div>
                    <button type="button" onClick={() => toggle(s.cohort_id)} className={cn('shrink-0 text-[11px] font-semibold rounded-full border px-2 py-0.5 transition-colors', isDark ? 'border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/15' : 'border-teal-300 text-teal-800 hover:bg-teal-100')} data-testid={`program-cohort-suggest-${s.cohort_id}`}>+ Link</button>
                  </li>
                ))}
              </ul>
              <p className={cn('mt-1.5 text-[10px]', isDark ? 'text-slate-500' : 'text-slate-500')}>Based on name and description matches, type and timeline overlap, shared measure activity{suggestions.some((s) => /shared learner/.test(s.reasons.join(' '))) ? ' and shared learners from rosters' : ''}. Upload a roster (⇧ icon) to add real learner overlap.</p>
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto space-y-1 pr-1" data-testid="program-cohort-list">
            {cohorts.length === 0 && <li className="text-xs text-slate-500 py-4 text-center">No cohorts in this organization yet.</li>}
            {cohorts.map((c) => {
              const on = selected.has(c.id);
              const sg = suggestionById[c.id];
              const rc = rosterCounts[c.id] ?? sg?.roster_count ?? 0;
              return (
                <li key={c.id} className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={cn('flex-1 min-w-0 flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      on ? (isDark ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100' : 'border-teal-300 bg-teal-50 text-teal-900')
                         : (isDark ? 'border-[#22304d] hover:bg-white/5 text-slate-300' : 'border-slate-200 hover:bg-slate-50 text-slate-700'))}
                    aria-pressed={on}
                    data-testid={`program-cohort-toggle-${c.id}`}
                  >
                    <span className="truncate flex items-center gap-1.5">{c.name}{sg && !on && <Sparkles className="h-3 w-3 opacity-60" title={sg.reasons.join(' · ')} />}
                      {rc > 0 && <span className={cn('text-[10px] tabular-nums', isDark ? 'text-slate-400' : 'text-slate-500')} data-testid={`program-cohort-roster-count-${c.id}`}>{rc} learners</span>}
                    </span>
                    <span className={cn('w-5 h-5 rounded-md border flex items-center justify-center shrink-0', on ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300')}>{on && <Check className="h-3.5 w-3.5" />}</span>
                  </button>
                  <label title="Import learner roster (CSV with email / learner_id, name)" className={cn('shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center cursor-pointer transition-colors', isDark ? 'border-[#22304d] text-slate-400 hover:text-cyan-200 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:text-teal-700 hover:bg-teal-50')} data-testid={`program-cohort-roster-upload-${c.id}`}>
                    {rosterBusy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    <input type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importRoster(c.id, f); }} data-testid={`program-cohort-roster-input-${c.id}`} />
                  </label>
                </li>
              );
            })}
          </ul>
          </>
        )}
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <span className="text-xs text-slate-500" data-testid="program-cohort-count">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="program-cohort-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || loading} data-testid="program-cohort-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Save links</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
